import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import * as yup from 'yup'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormHelperText,
  Grid,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import ReactQuill from 'react-quill'
import { decode, encode } from 'html-entities'
import Dialog from 'components/dialog/dialog'
// import type { PartialVoucherCreateDataProps, PartialVoucherCreateProps } from 'modules/partial'
import type { CallbackHandler, DetailStateProps, ErrorProps } from 'modules/types'
import FORMIK from 'modules/formik'
import {
  useGetVoucherCategoryDropdownMutation,
  useGetVoucherSubCategoryDropdownMutation
} from 'store/voucherCategory'
import GlobalStyle from 'modules/styles'
import { useGetTokenPartnerDropdownMutation } from 'store/token'
import { useGetTokenCategoryDropdownMutation } from 'store/tokenRequest'
import GLOBAL from 'modules/global'
import { useUpdateListVoucherMutation, useGetListVoucherDetailQuery } from 'store/voucher'
import { useGetCarModelDropdownListMutation } from 'store/dropdown'
import { useGetCitiesDropdownMutation, useGetProvinciesDropdownMutation } from 'store/region'
import Notification from 'components/notification'
import Loading from 'components/loading/loading'
import BackdropImage from 'components/backdropImage/backdropImage'
import DialogStyle from './style'

const Edit = ({ open, id, onClose }: DetailStateProps & { id: string | number }) => {
  if (!open) return <></>

  const { data, isFetching, isLoading } = useGetListVoucherDetailQuery(id)
  const loading = !data && (isLoading || isFetching)

  const [getVoucherCategoryDropdown, dropdownCategory] = useGetVoucherCategoryDropdownMutation()
  const [getVoucherSubCategoryDropdown, dropdownSubCategory] =
    useGetVoucherSubCategoryDropdownMutation()
  const [getTokenPartnerDropdown, partnerDropdown] = useGetTokenPartnerDropdownMutation()
  const [getTokenCategoryDropdown, tokenCategoryDropdown] = useGetTokenCategoryDropdownMutation()
  const [getCarModelDropdownList, carModelList] = useGetCarModelDropdownListMutation()
  const [getProvinceDropdown, provinceDropdown] = useGetProvinciesDropdownMutation()
  const [getCityDropdown, cityDropdown] = useGetCitiesDropdownMutation()
  const [updateVoucher, update] = useUpdateListVoucherMutation()

  const [prevRegistrationDateFrom, setPrevRegistrationDateFrom] = useState<string>('');
  const [prevRegistrationDateTo, setPrevRegistrationDateTo] = useState<string>('');
  const [DoneChangesDateToValue, setDoneChangesDateTo] = useState<boolean>(false);
  const [isMaxDateDisabled, setIsMaxDateDisabled] = useState<boolean>(true);

  const [previewImage, setPreviewImage] = useState<string | ArrayBuffer | null>(null)
  const [previewBanner, setPreviewBanner] = useState<string | ArrayBuffer | null>(null)
  const [previewBackdrop, setPreviewBackdrop] = useState<boolean>(false)
  const [bannerBackdrop, setBannerBackdrop] = useState<boolean>(false)
  const [carModel, setCarModel] = useState<CarModelDropDown[]>([])
  const [userTypeProvince, setUserTypeProvince] = useState<LocationDropDown[]>([])
  const [userTypeCity, setUserTypeCity] = useState<LocationDropDown[]>([])
  const [messageUntil, setMessageUntil] = useState<string>('')
  const [isClearDisabled, setIsClearDisabled] = useState<boolean>(true);

  const [getMinimum, setMinimum] = useState<string>('')
  const [getMaximum, setMaximum] = useState<string>('')

  const scheme = yup.object<PartialVoucherCreateProps>({
    image: yup.mixed().required('Voucher Image is required'),
    banner: yup.mixed().required('Voucher Image Banner is required'),
    data: yup.object({
      nameEn: yup.string().required('Name in English is required'),
      nameId: yup.string().required('Name in Indonesia is required'),
      voucherType: yup.string().required('Voucher Type is required'),
      bookingType: yup.string().when('voucherType', {
        is: (val: string) => val === 'Booking',
        then: (schema) => schema.required('Booking Type is required')
      }),
      voucherCategoryId: yup.number().required('Voucher Category ID is required'),
      voucherSubCategoryId: yup.number().required('Voucher Sub Category ID is required'),
      voucherTokenCategoryId: yup.number().required('Voucher Token Category ID is required'),
      partnerId: yup.number().required('Partner ID is required'),
      applicableAreaMode: yup.string().required('Applicable Area Mode is required'),
      termsAndConditionsEn: yup.string().required('Terms and Condition in English is required'),
      termsAndConditionsId: yup.string().required('Terms and Condition in Indonesia is required'),
      informationEn: yup.string().required('Information in English is required'),
      informationId: yup.string().required('Information in Indonesia is required'),
      generateType: yup.string(),
      description: yup.string(),
      payloadGenerateRequest: yup
        .object({
          requestName: yup.string(),
          quantity: yup.string(),
          validFrom: yup.string(),
          validUntil: yup.string()
        })
        .notRequired(),
      carModels: yup.array().of(yup.object().shape({
        id: yup.number().required(),
        name: yup.string().required(),
      })).nullable(),
      locations: yup.array().of(yup.object().shape({
        cityId: yup.number().required(),
        cityName: yup.string().notRequired(),
        provinceId: yup.number().notRequired(),
        provinceName: yup.string().notRequired(),
      })).nullable(),
      registrationDateFrom: yup.string().nullable(),
      registrationDateTo: yup.string().nullable(),
    }),
    importToken: yup.mixed().notRequired()
  })

  const initialValues: PartialVoucherCreateProps = {
    image: {} as File,
    banner: {} as File,
    importToken: {} as File,
    data: {
      id: Number(id),
      nameEn: (data && data.nameEn) || '',
      nameId: (data && data.nameId) || '',
      voucherType: (data && data.voucherType) || '-',
      bookingType: (data && data.bookingType) || '-',
      voucherCategoryId: (data && data.voucherCategoryId) || null,
      voucherSubCategoryId: (data && data.voucherSubCategoryId) || null,
      voucherTokenCategoryId: (data && data.tokenCategoryId) || null,
      partnerId: (data && data.partnerId) || null,
      applicableAreaMode: (data && data.applicableAreaMode) || '',
      termsAndConditionsEn: (data && decode(data.termsAndConditionsEn)) || '',
      termsAndConditionsId: (data && decode(data.termsAndConditionsId)) || '',
      informationEn: (data && decode(data.descriptionEn)) || '',
      informationId: (data && decode(data.descriptionId)) || '',
      generateType: (data && data.generateType) || 'None',
      visibility: data && data.visibility,
      description: '',
      payloadGenerateRequest: {
        requestName: '',
        quantity: '',
        validFrom: '',
        validUntil: ''
      },
      carModels: (data && data.carModels && data.carModels.length > 0) ? data.carModels.map((item) => ({
        id: item.id,
        name: item.name
      })) : [],
      locations: (data && data.locations && data.locations.length > 0) ? data.locations.map((item) => ({
        cityId: item.cityId,
        cityName: item.cityName,
        provinceId: item.provinceId,
        provinceName: item.provinceName,
      })) : [],
      registrationDateFrom: (data && GLOBAL.formatDateddMMYYLongDateDetail(data.registrationDateFrom)) || '',
      registrationDateTo: (data && GLOBAL.formatDateddMMYYLongDateDetail(data.registrationDateTo)) || ''
    }
  }

  const formik = FORMIK.useFormFormik<PartialVoucherCreateProps>(
    scheme,
    initialValues,
    (values) => {
      const formData = new FormData()

      const carModels = carModel.map(carModel => carModel.id);

      const formatDateWithTime = (dateStr: string | undefined | null, time: string) => {
        if (!dateStr) return ""
        const datePart = dayjs(dateStr).format('YYYY-MM-DD')
        return `${datePart} ${time}`
      }

      const data = {
        ...values.data,
        generateType: values.data.generateType === 'None' ? null : values.data.generateType,
        termsAndConditionsEn: encode(values.data.termsAndConditionsEn),
        termsAndConditionsId: encode(values.data.termsAndConditionsId),
        informationEn: encode(values.data.informationEn),
        informationId: encode(values.data.informationId),
        carModels: carModels,
        locations: values.data.locations,
        // registrationDateFrom: (values.data.registrationDateFrom && values.data.registrationDateFrom !== "-") ? values.data.registrationDateFrom : "",
        // registrationDateTo: (values.data.registrationDateTo && values.data.registrationDateTo !== "-") ? values.data.registrationDateTo : "",
        // registrationDateFrom: formatDateWithTime(values.data.registrationDateFrom, "00:00:00"),
        // registrationDateTo: formatDateWithTime(values.data.registrationDateTo, "23:59:59"),
        registrationDateFrom: values.data.registrationDateFrom === "-" ? "" : formatDateWithTime(values.data.registrationDateFrom, "00:00:00"),
        registrationDateTo: values.data.registrationDateTo === "-" ? "" : formatDateWithTime(values.data.registrationDateTo, "23:59:59"),
      }

      if (values.image) formData.append('image', values.image)
      if (values.banner) formData.append('banner', values.banner)

      formData.append('data', JSON.stringify(data))
      // console.log('payloadddddd', JSON.stringify(data));

      updateVoucher(formData)
    }
  )

  const fileOnChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: string,
    callback: CallbackHandler<string | ArrayBuffer | null>
  ) => {
    const files = e && e.target && e.target.files
    const notEmpty = files && files.length > 0 && !isEmpty(files)

    if (notEmpty) {
      const reader = new FileReader()

      reader.onload = () => callback(reader.result)
      reader.readAsDataURL(files[0])

      formik.setFieldValue(field, files[0])
      return
    }

    formik.setFieldValue(field, {} as File)
    return callback(null)
  }

  const isEndDateGreaterThanStartDate = () => {
    if (
      messageUntil && dayjs(formik.values.data.registrationDateFrom).isAfter(dayjs(formik.values.data.registrationDateTo))
    ) {
      return true
    } else {
      return false
    }
  }

  const error = formik.errors && formik.errors.data
  const touched = formik.touched && (formik.touched as PartialVoucherCreateDataProps)

  const checkVehicleRegistrationDateMinimum = () => {
    if (getMinimum && !getMaximum) {
      return true
    } else if (!getMinimum && getMaximum) {
      return true
    } else {
      return false
    }
  }

  const handleRegistrationDateFromChange = (newDate: Dayjs | null) => {
    const newDateValue = (!isEmpty(newDate) && newDate?.format('YYYY-MM-DD HH:mm:ss')) || '';
    formik.setFieldValue('data.registrationDateFrom', newDateValue);
    setMinimum(newDateValue);

    // Clear Maximum date if Minimum date is cleared
    if (!newDateValue) {
      formik.setFieldValue('data.registrationDateTo', '');
      setMaximum('');
      setPrevRegistrationDateTo('');
      setDoneChangesDateTo(true);
      setIsMaxDateDisabled(false);
    } else {
      if (prevRegistrationDateFrom !== newDateValue) {
        formik.setFieldValue('data.registrationDateTo', '');
        setPrevRegistrationDateTo('');
        setDoneChangesDateTo(true);
        setIsMaxDateDisabled(false);
      }
    }
    setPrevRegistrationDateFrom(newDateValue);
  };

  const handleRegistrationDateToChange = (newDate: Dayjs | null) => {
    const newDateValue = (!isEmpty(newDate) && newDate?.format('YYYY-MM-DD HH:mm:ss')) || '';
    formik.setFieldValue('data.registrationDateTo', newDateValue);
    setMaximum(newDateValue);

    // Do not set Maximum date if Minimum date is cleared
    if (!prevRegistrationDateFrom) {
      setMaximum('');
    }

    if (prevRegistrationDateTo !== newDateValue) {
      setDoneChangesDateTo(false);
    }
    setPrevRegistrationDateTo(newDateValue);
  };
  // const disabled = !formik.isValid || isEmpty(previewImage) || isEmpty(previewBanner) ||
  //   isEndDateGreaterThanStartDate() || (userTypeProvince.length > 0 && userTypeCity.length === 0) || checkSignUpDateMinimum();

  // console.log('formik', formik.values);
  // console.log('isDisable ===>', !formik.isValid ||
  //   isEmpty(previewImage) ||
  //   isEmpty(previewBanner) ||
  //   isEndDateGreaterThanStartDate() ||
  //   (userTypeProvince.length > 0 && userTypeCity.length === 0) ||
  //   checkSignUpDateMinimum() ||
  //   (prevRegistrationDateFrom && prevRegistrationDateTo && dayjs(prevRegistrationDateFrom).isAfter(dayjs(prevRegistrationDateTo))) ||
  //   false || DoneChangesDateToValue)

  const disabled = !formik.isValid ||
    isEmpty(previewImage) ||
    isEmpty(previewBanner) ||
    isEndDateGreaterThanStartDate() ||
    (userTypeProvince.length > 0 && userTypeCity.length === 0) ||
    checkVehicleRegistrationDateMinimum() ||
    (prevRegistrationDateFrom && prevRegistrationDateTo && dayjs(prevRegistrationDateFrom).isAfter(dayjs(prevRegistrationDateTo))) ||
    false || DoneChangesDateToValue;

  useEffect(() => {
    if (data) {
      setPrevRegistrationDateFrom(data.registrationDateFrom);
      setPrevRegistrationDateTo(data.registrationDateTo);

      if (data.voucherCategoryId) getVoucherCategoryDropdown()
      if (data.voucherSubCategoryId) getVoucherSubCategoryDropdown({ id: data.voucherCategoryId })
      if (data.partnerId) getTokenPartnerDropdown()
      if (data.tokenCategoryId) getTokenCategoryDropdown()
      if (data.image) setPreviewImage(data.image)
      if (data.banner) setPreviewBanner(data.banner)
      if (data.carModels) {
        getCarModelDropdownList()
        const defaultCarModel =
          (data &&
            data.carModels &&
            data.carModels.map((item) => ({
              id: item.id,
              name: item.name
            }))) ||
          []
        setCarModel(defaultCarModel)
      }
      if (data.locations) {
        const defaultProvince =
          (data &&
            data.locations &&
            data.locations.map((item) => ({
              cityId: 0,
              cityName: '',
              provinceId: item.provinceId,
              provinceName: item.provinceName
            }))) ||
          []
        setUserTypeProvince(defaultProvince)
        const defaultCity =
          (data &&
            data.locations &&
            data.locations.map((item) => ({
              cityId: item.cityId,
              cityName: item.cityName,
              provinceId: item.provinceId,
              provinceName: item.provinceName
            }))) ||
          []
        setUserTypeCity(defaultCity)
      }
    }
  }, [data])

  useEffect(() => {
    setIsClearDisabled(disabled);
  }, [disabled]);

   // Function to handle province removal and update city dropdown
   const handleProvinceChange = (event: React.SyntheticEvent<Element, Event>, newValue: LocationDropDown[]) => {
    // Get the removed province IDs
    const removedProvinceIds = userTypeProvince
      .filter(province => !newValue.find(newProvince => newProvince.provinceId === province.provinceId))
      .map(province => province.provinceId);
    // Filter out cities associated with the removed provinces
    const updatedCities = userTypeCity.filter(
      city => !removedProvinceIds.includes(city.provinceId)
    );
    // Update the state for provinces and cities
    setUserTypeProvince(newValue);
    setUserTypeCity(updatedCities);

// console.log('updatedCities', updatedCities);


    // Update formik values for locations
    // const updatedLocations = newValue.map(province => ({
    //   cityId: 0,
    //   cityName: '', 
    //   provinceId: province.provinceId,
    //   provinceName: province.provinceName,
    // }));
    // console.log('updatedLocations', updatedLocations);
    
    // formik.setFieldValue('data.locations', updatedLocations);
    formik.setFieldValue('data.locations', updatedCities);
  };

  console.log('formikError', formik.errors)
  // console.log('formikTouched', formik.touched)

  return (
    <>
      <Dialog
        title='Edit Voucher'
        open={open}
        onCancel={onClose}
        onSubmit={() => formik.handleSubmit()}
        loading={update.isLoading}
        isDisabled={disabled}
        maxWidth='md'
      >
        <Container {...DialogStyle.Container}>
          {loading && <Loading />}
          {!loading && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ display: 'flex', flex: 1, gap: 2, flexDirection: 'column' }}>
                <TextField
                  id='nameEn'
                  variant='outlined'
                  label='Voucher EN-US Name'
                  value={formik.values.data.nameEn}
                  onChange={(event) => formik.setFieldValue('data.nameEn', event.target.value)}
                  onBlur={(event) => formik.setFieldValue('data.nameEn', event.target.value)}
                  error={touched && Boolean(touched.nameEn) && error && Boolean(error.nameEn)}
                  helperText={touched && touched.nameEn && error && error.nameEn}
                  fullWidth
                />
                <TextField
                  id='nameId'
                  variant='outlined'
                  label='Voucher ID Name'
                  value={formik.values.data.nameId}
                  onChange={(event) => formik.setFieldValue('data.nameId', event.target.value)}
                  onBlur={(event) => formik.setFieldValue('data.nameId', event.target.value)}
                  error={touched && Boolean(touched.nameId) && error && Boolean(error.nameId)}
                  helperText={touched && touched.nameId && error && error.nameId}
                  fullWidth
                />
                <Autocomplete
                  options={(dropdownCategory && dropdownCategory.data) || []}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option && value ? option.id == value.id : false
                  }
                  onOpen={() => getVoucherCategoryDropdown()}
                  onChange={(_, voucherCategory) => {
                    dropdownSubCategory.reset()
                    formik.setFieldValue(
                      'data.voucherCategoryId',
                      String((voucherCategory && voucherCategory.id) || null)
                    )
                  }}
                  value={
                    (dropdownCategory &&
                      dropdownCategory.data &&
                      dropdownCategory.data.find(
                        (e) => e.id == formik.values.data.voucherCategoryId
                      )) ||
                    null
                  }
                  ListboxProps={GlobalStyle.ListBox}
                  renderOption={(props, item) => (
                    <li {...props} key={item.id}>
                      {item.name}
                    </li>
                  )}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name='voucherCategoryId'
                      label='Category ID'
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {dropdownCategory.isLoading && (
                              <CircularProgress color='inherit' size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
                <Autocomplete
                  options={(dropdownSubCategory && dropdownSubCategory.data) || []}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option && value ? option.id == value.id : false
                  }
                  onOpen={() =>
                    formik.values.data.voucherCategoryId &&
                    getVoucherSubCategoryDropdown({ id: formik.values.data.voucherCategoryId })
                  }
                  onChange={(_, voucherCategory) =>
                    formik.setFieldValue(
                      'data.voucherSubCategoryId',
                      String((voucherCategory && voucherCategory.id) || null)
                    )
                  }
                  value={
                    (dropdownSubCategory &&
                      dropdownSubCategory.data &&
                      dropdownSubCategory.data.find(
                        (e) => e.id == formik.values.data.voucherSubCategoryId
                      )) ||
                    null
                  }
                  disabled={
                    formik.values.data.voucherCategoryId == null ||
                    formik.values.data.voucherCategoryId <= 0
                  }
                  ListboxProps={GlobalStyle.ListBox}
                  renderOption={(props, item) => (
                    <li {...props} key={item.id}>
                      {item.name}
                    </li>
                  )}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name='voucherSubCategoryId'
                      label='Voucher Sub Category ID'
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {dropdownSubCategory.isLoading && (
                              <CircularProgress color='inherit' size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
                <Autocomplete
                  options={(partnerDropdown && partnerDropdown.data) || []}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option && value ? option.id == value.id : false
                  }
                  onOpen={() => getTokenPartnerDropdown()}
                  onChange={(_, partner) =>
                    formik.setFieldValue('data.partnerId', String((partner && partner.id) || null))
                  }
                  value={
                    (partnerDropdown &&
                      partnerDropdown.data &&
                      partnerDropdown.data.find((e) => e.id == formik.values.data.partnerId)) ||
                    null
                  }
                  ListboxProps={GlobalStyle.ListBox}
                  renderOption={(props, item) => (
                    <li {...props} key={item.id}>
                      {item.name}
                    </li>
                  )}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name='partnerId'
                      label='Partner'
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {partnerDropdown.isLoading && (
                              <CircularProgress color='inherit' size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
                <Autocomplete
                  options={(tokenCategoryDropdown && tokenCategoryDropdown.data) || []}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option && value ? option.id == value.id : false
                  }
                  onOpen={() => getTokenCategoryDropdown()}
                  onChange={(_, tokenCategory) =>
                    formik.setFieldValue(
                      'data.voucherTokenCategoryId',
                      String((tokenCategory && tokenCategory.id) || null)
                    )
                  }
                  value={
                    (tokenCategoryDropdown &&
                      tokenCategoryDropdown.data &&
                      tokenCategoryDropdown.data.find(
                        (e) => e.id == formik.values.data.voucherTokenCategoryId
                      )) ||
                    null
                  }
                  ListboxProps={GlobalStyle.ListBox}
                  renderOption={(props, item) => (
                    <li {...props} key={item.id}>
                      {item.name}
                    </li>
                  )}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name='tokenCategoryId'
                      label='Token Category'
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {tokenCategoryDropdown.isLoading && (
                              <CircularProgress color='inherit' size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
                <Grid item xs={12}>
                  <Typography {...DialogStyle.Title}>Description ID</Typography>
                  <ReactQuill
                    theme='snow'
                    value={formik.values.data.informationId}
                    onChange={(e) =>
                      formik.setFieldValue('data.informationId', GLOBAL.emptyQuill(e))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography {...DialogStyle.Title}>Description EN-US</Typography>
                  <ReactQuill
                    theme='snow'
                    value={formik.values.data.informationEn}
                    onChange={(e) =>
                      formik.setFieldValue('data.informationEn', GLOBAL.emptyQuill(e))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography {...DialogStyle.Title}>Terms and Condition ID</Typography>
                  <ReactQuill
                    theme='snow'
                    value={formik.values.data.termsAndConditionsId}
                    onChange={(e) =>
                      formik.setFieldValue('data.termsAndConditionsId', GLOBAL.emptyQuill(e))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography {...DialogStyle.Title}>Terms and Condition EN-US</Typography>
                  <ReactQuill
                    theme='snow'
                    value={formik.values.data.termsAndConditionsEn}
                    onChange={(e) =>
                      formik.setFieldValue('data.termsAndConditionsEn', GLOBAL.emptyQuill(e))
                    }
                  />
                </Grid>
              </Box>
              <Box sx={{ display: 'flex', flex: 1, gap: 2, flexDirection: 'column' }}>
                <TextField
                  id='voucherType'
                  variant='outlined'
                  label='Voucher Type'
                  value={formik.values.data.voucherType}
                  inputProps={{ readOnly: true }}
                  fullWidth
                />
                {formik.values.data.voucherType == 'Booking' && (
                  <TextField
                    id='voucherType'
                    variant='outlined'
                    label='Booking Type'
                    value={formik.values.data.bookingType}
                    inputProps={{ readOnly: true }}
                    fullWidth
                  />
                )}
                {/* <FormControl fullWidth>
                  <InputLabel>Applicable Area Mode</InputLabel>
                  <Select
                    labelId='applicableAreaMode'
                    id='applicableAreaMode'
                    variant='outlined'
                    label='Applicable Area Mode'
                    value={formik.values.data.applicableAreaMode}
                    onChange={(event) =>
                      formik.setFieldValue('data.applicableAreaMode', event.target.value)
                    }
                    error={
                      touched &&
                      Boolean(touched.applicableAreaMode) &&
                      error &&
                      Boolean(error.applicableAreaMode)
                    }
                  >
                    <MenuItem value='' disabled />
                    <MenuItem value='All Branches'>All Branches</MenuItem>
                    <MenuItem value='Specific Branches'>Specific Branches</MenuItem>
                  </Select>
                  {touched && touched.applicableAreaMode && error && (
                    <FormHelperText error id='applicableAreaMode'>
                      {error.applicableAreaMode}
                    </FormHelperText>
                  )}
                </FormControl> */}
                <TextField
                  id='image'
                  label='Voucher Image *'
                  type='file'
                  placeholder='Image'
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ accept: 'image/png, image/gif, image/jpeg' }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    fileOnChange(e, 'image', (value) => setPreviewImage(value))
                  }
                  onBlur={formik.handleBlur}
                  fullWidth
                />
                {previewImage && (
                  <Grid item xs={12}>
                    <Typography {...DialogStyle.Title}>Voucher Image</Typography>
                    <Box {...DialogStyle.Image}>
                      <BackdropImage
                        open={previewBackdrop}
                        onOpen={() => setPreviewBackdrop(true)}
                        onClose={() => setPreviewBackdrop(false)}
                      >
                        <img
                          src={previewImage as string | undefined}
                          alt='Voucher Image'
                          width='100%'
                        />
                      </BackdropImage>
                    </Box>
                  </Grid>
                )}
                <TextField
                  id='banner'
                  label='Voucher Image Banner *'
                  type='file'
                  placeholder='Image Banner'
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ accept: 'image/png, image/gif, image/jpeg' }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    fileOnChange(e, 'banner', (value) => setPreviewBanner(value))
                  }
                  onBlur={formik.handleBlur}
                  fullWidth
                />
                {previewBanner && (
                  <Grid item xs={12}>
                    <Typography {...DialogStyle.Title}>Voucher Image Banner</Typography>
                    <Box {...DialogStyle.Image}>
                      <BackdropImage
                        open={bannerBackdrop}
                        onOpen={() => setBannerBackdrop(true)}
                        onClose={() => setBannerBackdrop(false)}
                      >
                        <img
                          src={previewBanner as string | undefined}
                          alt='Voucher Image Banner'
                          width='100%'
                        />
                      </BackdropImage>
                    </Box>
                  </Grid>
                )}
                <Autocomplete
                  fullWidth
                  sx={{ marginBottom: '10px' }}
                  multiple
                  id='carModelList'
                  value={carModel}
                  onOpen={() => getCarModelDropdownList()}
                  isOptionEqualToValue={(option, value) =>
                    option && value ? option.name == value.name : false
                  }
                  onChange={(event, newValue) => {
                    setCarModel(newValue)
                  }}
                  options={(carModelList && carModelList.data) || []}
                  getOptionLabel={(option) => option.name}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip label={option.name} {...getTagProps({ index })} key={index} />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} label='Car Model' />}
                />
                <Autocomplete
                  fullWidth
                  sx={{ marginBottom: '10px' }}
                  multiple
                  id='fixed-tags-demo'
                  value={userTypeProvince}
                  onOpen={() => getProvinceDropdown()}
                  isOptionEqualToValue={(option, value) =>
                    option && value ? option.provinceName == value.provinceName : false
                  }
                  // onChange={(event, newValue) => {
                  //   setUserTypeProvince(newValue);
                  //   const updatedLocations = newValue.map(province => ({
                  //     cityId: 0, // Default or selected city ID
                  //     cityName: '', // Default or selected city name
                  //     : province.provinceId,
                  //     provinceName: province.provinceName,
                  //   }));
                  //   formik.setFieldValue('data.locations', updatedLocations);
                  // }}
                  onChange={handleProvinceChange}
                  options={(provinceDropdown && provinceDropdown.data) || []}
                  getOptionLabel={(option) => option.provinceName}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip label={option.provinceName} {...getTagProps({ index })} key={index} />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} label='Province' />}
                />
                <Autocomplete
                  fullWidth
                  sx={{ marginBottom: '10px' }}
                  multiple
                  id='fixed-tags-demo'
                  value={userTypeCity}
                  onOpen={() => {
                    const selectedProvinceIds = userTypeProvince.map(province => province.provinceId);
                    getCityDropdown(selectedProvinceIds);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option && value ? option.cityName == value.cityName : false
                  }
                  onChange={(event, newValue) => {
                    setUserTypeCity(newValue);
                    // Update locations with selected cities
                    const updatedLocations = newValue.map(city => ({
                      cityId: city.cityId,
                      cityName: city.cityName,
                      provinceId: city.provinceId, // Assuming the first selected province
                      provinceName: city.provinceName // Assuming the first selected province
                    }));
                    formik.setFieldValue('data.locations', updatedLocations);
                  }}
                  options={(cityDropdown && cityDropdown.data) || []}
                  // getOptionLabel={(option) => option.cityName}
                  getOptionLabel={(option) => `${option.cityName} - ${option.provinceName}`}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      // <Chip label={option.cityName} {...getTagProps({ index })} key={index} />
                      <Chip label={`${option.cityName} - ${option.provinceName}`} {...getTagProps({ index })} key={index} />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} label='City' />}
                  disabled={userTypeProvince.length === 0} // Disable if no province is selected
                />
                <Typography><strong>Vehicle Registration Date</strong></Typography>
                <div style={{ justifyContent: 'space-between', flexDirection: 'row', display: 'flex' }}>
                  {data?.registrationDateFrom === null || isEmpty(data?.registrationDateFrom) || data?.registrationDateFrom === '-' ? (
                    <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                      <DatePicker
                        value={null}
                        onChange={(e: Dayjs | null) => {
                          const newDate = (!isEmpty(e) && e?.format('YYYY-MM-DD HH:mm:ss')) || ''
                          formik.setFieldValue('data.registrationDateFrom', newDate)
                          setMinimum(newDate);
                        }}
                        label='Minimum'
                        format='DD MMM YYYY'
                        sx={{ width: '47%' }}
                      />
                    </LocalizationProvider>
                  ) : (
                    <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                      <DatePicker
                        value={formik.values.data && formik.values.data.registrationDateFrom ? dayjs(formik.values.data.registrationDateFrom) : null}
                        // onChange={(e: Dayjs | null) => {
                        //   const newDate = (!isEmpty(e) && e?.format('YYYY-MM-DD HH:mm:ss')) || ''
                        //   formik.setFieldValue('data.registrationDateFrom', newDate)
                        //   setMinimum(newDate);
                        // }}
                        onChange={handleRegistrationDateFromChange}
                        label='Minimum'
                        format='DD MMM YYYY'
                        sx={{ width: '47%' }}
                      />
                    </LocalizationProvider>
                  )}
                  {data?.registrationDateTo === null || isEmpty(data?.registrationDateTo) || data?.registrationDateTo === '-' ? (
                    <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                      <DatePicker
                        value={null}
                        minDate={formik.values.data && formik.values.data.registrationDateFrom ? dayjs(formik.values.data.registrationDateFrom) : undefined}
                        onChange={(e: Dayjs | null) => {
                          const newDate = (!isEmpty(e) && e?.format('YYYY-MM-DD HH:mm:ss')) || ''
                          formik.setFieldValue('data.registrationDateTo', newDate)
                          setMaximum(newDate);
                        }}
                        onError={(error) => {
                          if (error) {
                            formik.setFieldError('data.registrationDateTo', error);
                            setMessageUntil(formik.values.data.registrationDateFrom)
                          } else if (formik.values.data.registrationDateFrom && dayjs(formik.values.data.registrationDateFrom).isAfter(dayjs(formik.values.data.registrationDateTo))) {
                            formik.setFieldError('data.registrationDateTo', 'Maximum Date must be greater than Minimum Date');
                          }
                        }}
                        label='Maximum'
                        format='DD MMM YYYY'
                        sx={{ width: '47%' }}
                      />
                    </LocalizationProvider>
                  ) : (
                    <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                      <DatePicker
                        value={formik.values.data && formik.values.data.registrationDateTo ? dayjs(formik.values.data.registrationDateTo) : null}
                        minDate={formik.values.data && formik.values.data.registrationDateFrom ? dayjs(formik.values.data.registrationDateFrom) : undefined}
                        // onChange={(e: Dayjs | null) => {
                        //   const newDate = (!isEmpty(e) && e?.format('YYYY-MM-DD HH:mm:ss')) || ''
                        //   formik.setFieldValue('data.registrationDateTo', newDate)
                        //   setMaximum(newDate);
                        // }}
                        disabled={isMaxDateDisabled}
                        onChange={handleRegistrationDateToChange}
                        onError={(error) => {
                          if (error) {
                            formik.setFieldError('data.registrationDateTo', error);
                            setMessageUntil(formik.values.data.registrationDateFrom)
                          } else if (formik.values.data.registrationDateFrom && dayjs(formik.values.data.registrationDateFrom).isAfter(dayjs(formik.values.data.registrationDateTo))) {
                            formik.setFieldError('data.registrationDateTo', 'Maximum Date must be greater than Minimum Date');
                          }
                        }}
                        label='Maximum'
                        format='DD MMM YYYY'
                        sx={{ width: '47%', marginLeft: 2 }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => {
                          formik.setFieldValue('data.registrationDateFrom', null); // Clear the date
                          setMinimum(''); // Clear the minimum state
                          formik.setFieldValue('data.registrationDateTo', null); // Clear the date
                          setMaximum(''); // Clear the maximum state
                        }}

                        disabled={isClearDisabled}
                        sx={{
                          padding: '4px',
                          minWidth: 'auto',
                          height: '26px',
                          width: '28px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginLeft: 1,
                          marginTop: 1.2,
                          backgroundColor: isClearDisabled ? '#E8E8E8' : 'darkgray',
                          borderColor: isClearDisabled ? '#E8E8E8' : 'darkgray',
                          color: isClearDisabled ? 'white' : 'white',
                          '&:hover': {
                            backgroundColor: isClearDisabled ? '#E8E8E8' : 'darkgray',
                            borderColor: isClearDisabled ? '#E8E8E8' : 'darkgray',
                            color: isClearDisabled ? 'white' : 'white',
                          },
                        }}
                      >
                        <Tooltip title="Clear" arrow>
                          <span style={{ fontSize: '1.3rem' }}>x</span>
                        </Tooltip>
                      </Button>
                    </LocalizationProvider>
                  )}
                </div>
                {messageUntil && dayjs(formik.values.data.registrationDateFrom).isAfter(dayjs(formik.values.data.registrationDateTo)) && (
                  <FormHelperText sx={{ alignSelf: 'center', marginTop: -1.5 }} error id='registrationDateTo'>Maximum Date must be greater than Minimum Date</FormHelperText>
                )}
              </Box>
            </Box>
          )}
        </Container>
      </Dialog>
      <Notification
        open={!update.isLoading && !update.isUninitialized}
        onClose={() => (update.isError ? update.reset() : location.reload())}
        isError={Boolean(update.error) && update.isError}
        message={GLOBAL.returnExceptionMessage(update.isError, update.error as ErrorProps)}
      />
    </>
  )
}

export default Edit


export type PartialVoucherCreateProps = {
  image: File
  banner: File
  data: PartialVoucherCreateDataProps
  importToken: File
}

export type PartialVoucherCreateDataProps = {
  id?: number
  description: string
  nameEn: string
  nameId: string
  voucherType: string
  bookingType: string
  voucherCategoryId: number | null
  voucherSubCategoryId: number | null
  voucherTokenCategoryId: number | null
  partnerId: number | null
  applicableAreaMode: string | null
  informationEn: string
  informationId: string
  termsAndConditionsEn: string
  termsAndConditionsId: string
  generateType: string
  // approvalDate: string
  visibility?: string
  payloadGenerateRequest?: PartialVoucherCreateDataRequestProps
  carModels: CarModelDropDown[]
  locations: LocationDropDown[]
  registrationDateFrom: string
  registrationDateTo: string
}

export type PartialVoucherCreateDataRequestProps = {
  requestName: string
  quantity: string
  validFrom: string
  validUntil: string
}

export type LocationDropDown = {
  cityId: number
  cityName: string
  provinceId: number
  provinceName: string
}

export type CarModelDropDown = {
  id: number
  name: string
}